import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  motion,
  useScroll,
  useTransform,
  useMotionValue,
  useSpring,
  AnimatePresence,
} from "framer-motion";
import {
  Heart,
  ArrowRight,
  Mail,
  Loader2,
  Sparkles,
  Brain,
  Crown,
  Shield,
  Flame,
  Camera,
  MessageCircle,
  X,
  Star,
  Lock,
  Zap,
  Eye,
  Target,
  ChevronDown,
  Check,
  Quote,
  Volume2,
  VolumeX,
} from "lucide-react";
import { API } from "../context/AuthContext";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../i18n/LangContext";
import { useTheme } from "../context/ThemeContext";
import { toast } from "sonner";

export default function Landing() {
  const { t, lang } = useLang();
  const { resolved } = useTheme();
  const { submitLead, ready } = useAuth();
  const navigate = useNavigate();
  const heroRef = useRef(null);

  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const audioRef = useRef(null);
  const [musicEnabled, setMusicEnabled] = useState(() => {
    try {
      return localStorage.getItem("homeMusicEnabled") === "true";
    } catch {
      return false;
    }
  });

  const proceed = () => navigate("/login");

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email || !email.includes("@")) {
      toast.error(t("landingEmailInvalid"));
      return;
    }
    setLoading(true);
    try {
      await submitLead(email);
      try {
        await axios.post(`${API}/newsletter/subscribe`, {
          email,
          lang,
          source: "landing-hero",
        });
      } catch {/* non-blocking */}
      // Redirect to registration with email prefilled so the user actually creates
      // an account (rather than a global guest session).
      navigate(`/register?email=${encodeURIComponent(email)}`);
    } catch (err) {
      toast.error(err.response?.data?.detail?.[0]?.msg || err.message || "Error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    try {
      localStorage.setItem("homeMusicEnabled", musicEnabled ? "true" : "false");
    } catch {
      // localStorage may be unavailable in private modes
    }

    const audio = audioRef.current;
    if (!audio) return;

    audio.volume = 0.35;

    if (musicEnabled) {
      audio.play().catch(() => {
        // Browser may block autoplay until the user clicks the page.
      });
    } else {
      audio.pause();
    }
  }, [musicEnabled]);

  useEffect(() => {
    const unlockAudio = () => {
      const audio = audioRef.current;
      if (musicEnabled && audio) {
        audio.play().catch(() => {});
      }
    };

    document.addEventListener("click", unlockAudio);
    return () => document.removeEventListener("click", unlockAudio);
  }, [musicEnabled]);

  const toggleMusic = () => {
    setMusicEnabled((prev) => !prev);
  };

  // Parallax for hero orbs
  const { scrollY } = useScroll();
  const orbY1 = useTransform(scrollY, [0, 600], [0, -120]);
  const orbY2 = useTransform(scrollY, [0, 600], [0, 80]);

  const isLight = resolved === "light";

  return (
    <div
      className="relative overflow-hidden"
      data-testid="landing-page"
      style={{ background: "var(--dc-bg)", color: "var(--dc-text)" }}
    >
      <audio ref={audioRef} loop preload="auto">
        <source src="/music/serce.mp3" type="audio/mpeg" />
      </audio>

      <MusicToggle enabled={musicEnabled} onClick={toggleMusic} />
      {/* Animated background */}
      <div className="pointer-events-none absolute inset-0 -z-10">
        <motion.div
          style={{ y: orbY1 }}
          className="absolute -top-32 -left-32 h-[26rem] w-[26rem] rounded-full blur-3xl dc-softpulse"
        >
          <div
            className="h-full w-full rounded-full"
            style={{
              background:
                "radial-gradient(circle, rgba(255,68,88,0.30), rgba(253,38,122,0.18), transparent)",
            }}
          />
        </motion.div>
        <motion.div
          style={{ y: orbY2 }}
          className="absolute -bottom-32 -right-24 h-[28rem] w-[28rem] rounded-full blur-3xl dc-softpulse"
        >
          <div
            className="h-full w-full rounded-full"
            style={{
              background:
                "radial-gradient(circle, rgba(253,176,34,0.22), rgba(255,96,54,0.14), transparent)",
            }}
          />
        </motion.div>
        <div className="absolute inset-0 dc-grid-bg" />
      </div>

      {/* HERO */}
      <section
        ref={heroRef}
        className="mx-auto grid max-w-7xl gap-12 px-4 py-16 sm:px-6 lg:grid-cols-2 lg:gap-16 lg:px-8 lg:py-24"
      >
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          className="flex flex-col justify-center"
        >
          <div className="mb-5 inline-flex w-fit items-center gap-2 rounded-full border border-[#ff4458]/30 bg-[#ff4458]/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-[#ff4458]">
            <Sparkles className="h-3.5 w-3.5" />
            <span data-testid="landing-eyebrow">{t("landingEyebrow")}</span>
          </div>

          <h1
            className="font-display text-4xl font-bold leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl"
            style={{ color: "var(--dc-text)" }}
          >
            {t("landingTitlePart1")}
            <br />
            <span className="dc-gradient-text">{t("landingTitlePart2")}</span>
          </h1>

          <p
            className="mt-6 max-w-xl text-base leading-relaxed sm:text-lg"
            style={{ color: "var(--dc-muted)" }}
          >
            {t("landingSubtitle")}
          </p>

          {/* Live stats strip */}
          <div className="mt-6 grid max-w-md grid-cols-3 gap-3" data-testid="landing-stats">
            {[
              { v: "10k+", l: lang === "pl" ? "Analiz" : "Analyses" },
              { v: "87%", l: lang === "pl" ? "Trafność" : "Accuracy" },
              { v: "4.8", l: lang === "pl" ? "Ocena" : "Rating" },
            ].map((s, i) => (
              <div
                key={i}
                className="rounded-2xl border px-3 py-2.5"
                style={{
                  borderColor: "var(--dc-border)",
                  background: "var(--dc-card)",
                }}
              >
                <div className="font-display text-xl font-extrabold dc-gradient-text">
                  {s.v}
                </div>
                <div
                  className="text-[10px] uppercase tracking-wider"
                  style={{ color: "var(--dc-muted)" }}
                >
                  {s.l}
                </div>
              </div>
            ))}
          </div>

          {/* Feature pills */}
          <div
            className="mt-6 flex flex-wrap gap-2"
            data-testid="landing-feature-pills"
          >
            {[
              { icon: Brain, label: t("landingFeatPersonality") },
              { icon: Camera, label: t("landingFeatVision") },
              { icon: MessageCircle, label: t("landingFeatChat") },
              { icon: Crown, label: t("landingFeatPremium") },
            ].map(({ icon: Icon, label }, i) => (
              <span
                key={i}
                className="inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs"
                style={{
                  borderColor: "var(--dc-border)",
                  background: "var(--dc-card)",
                  color: "var(--dc-text-soft)",
                }}
              >
                <Icon className="h-3.5 w-3.5 text-[#ff4458]" />
                {label}
              </span>
            ))}
          </div>

          {/* Email capture */}
          <form
            onSubmit={onSubmit}
            className="mt-8 max-w-md"
            data-testid="landing-email-form"
          >
            <label
              className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider"
              style={{ color: "var(--dc-muted)" }}
            >
              <Mail className="h-3.5 w-3.5" />
              {t("landingEmailLabel")}
            </label>
            <div className="flex flex-col gap-2 sm:flex-row">
              <input
                data-testid="landing-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t("landingEmailPlaceholder")}
                className="flex-1 rounded-full border px-5 py-3.5 outline-none transition-all focus:border-[#ff4458] focus:ring-2 focus:ring-[#ff4458]/30"
                style={{
                  borderColor: "var(--dc-border)",
                  background: isLight
                    ? "rgba(255,255,255,0.9)"
                    : "rgba(24,27,36,0.8)",
                  color: "var(--dc-text)",
                }}
              />
              <button
                type="submit"
                disabled={loading}
                data-testid="landing-email-submit"
                className="flex items-center justify-center gap-2 rounded-full dc-gradient-bg px-6 py-3.5 font-display text-sm font-semibold text-white shadow-[0_10px_30px_rgba(253,38,122,0.35)] transition-all hover:-translate-y-0.5 hover:shadow-[0_14px_36px_rgba(253,38,122,0.5)] disabled:cursor-not-allowed disabled:opacity-50"
              >
                {loading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Heart className="h-4 w-4" fill="white" />
                )}
                <span>{t("landingCta")}</span>
              </button>
            </div>
            <p
              className="mt-2.5 text-xs"
              style={{ color: "var(--dc-muted)" }}
            >
              {t("landingTrust")}
            </p>
          </form>

          <button
            type="button"
            onClick={proceed}
            disabled={!ready}
            data-testid="landing-skip"
            className="mt-5 inline-flex w-fit items-center gap-1.5 text-sm font-semibold underline-offset-4 transition-colors hover:underline disabled:opacity-50"
            style={{ color: "var(--dc-muted)" }}
          >
            {t("landingSkip")} <ArrowRight className="h-3.5 w-3.5" />
          </button>
        </motion.div>

        {/* Right: ANIMATED SWIPE STACK */}
        <motion.div
          initial={{ opacity: 0, x: 30, scale: 0.95 }}
          animate={{ opacity: 1, x: 0, scale: 1 }}
          transition={{ duration: 0.7, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
          className="relative mx-auto flex w-full max-w-md items-center justify-center"
        >
          <SwipeStackHero lang={lang} />
        </motion.div>
      </section>

      {/* HOW IT WORKS */}
      <section
        className="mx-auto max-w-6xl px-4 pb-20 sm:px-6 lg:px-8"
        data-testid="landing-how"
      >
        <SectionHeader
          eyebrow={lang === "pl" ? "Jak to działa" : "How it works"}
          title={t("landingHowTitle")}
          subtitle={t("landingHowSubtitle")}
        />
        <div className="mt-10 grid grid-cols-1 gap-5 md:grid-cols-3">
          {[
            { icon: Camera, title: t("landingStep1Title"), desc: t("landingStep1Desc") },
            { icon: Brain, title: t("landingStep2Title"), desc: t("landingStep2Desc") },
            { icon: Flame, title: t("landingStep3Title"), desc: t("landingStep3Desc") },
          ].map(({ icon: Icon, title, desc }, i) => (
            <motion.div
              key={i}
              data-testid={`landing-step-${i + 1}`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              className="dc-card relative overflow-hidden p-6 transition-all hover:-translate-y-1 hover:border-[#ff4458]/40"
            >
              <div
                className="mb-4 inline-flex items-center justify-center rounded-2xl p-3 text-[#ff4458]"
                style={{ background: "var(--dc-card-light)" }}
              >
                <Icon className="h-5 w-5" />
              </div>
              <div
                className="absolute right-5 top-5 font-display text-3xl font-bold"
                style={{ color: "var(--dc-card-light)" }}
              >
                0{i + 1}
              </div>
              <h3
                className="font-display text-lg font-semibold"
                style={{ color: "var(--dc-text)" }}
              >
                {title}
              </h3>
              <p
                className="mt-2 text-sm leading-relaxed"
                style={{ color: "var(--dc-muted)" }}
              >
                {desc}
              </p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* FEATURES GRID */}
      <section
        className="mx-auto max-w-6xl px-4 pb-20 sm:px-6 lg:px-8"
        data-testid="landing-features"
      >
        <SectionHeader
          eyebrow={lang === "pl" ? "Funkcje" : "Features"}
          title={
            lang === "pl"
              ? "Wszystko, czego potrzebujesz"
              : "Everything you need"
          }
          subtitle={
            lang === "pl"
              ? "Od pierwszej wiadomości po długą relację – AI ma plan."
              : "From first message to long-term – AI has a plan."
          }
        />
        <div className="mt-10 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
          {[
            {
              Icon: Eye,
              title: lang === "pl" ? "AI Vision na zdjęciach" : "AI Vision on photos",
              desc:
                lang === "pl"
                  ? "Wgraj screen profilu lub konwersacji – AI rozpozna kontekst i czyta emocje."
                  : "Drop a profile or chat screenshot – AI reads context and emotions.",
              hue: "#ff4458",
            },
            {
              Icon: Brain,
              title: lang === "pl" ? "Analiza osobowości" : "Personality analysis",
              desc:
                lang === "pl"
                  ? "Big Five + attachment style. Wiedz, z kim masz do czynienia."
                  : "Big Five + attachment style. Know who you're dealing with.",
              hue: "#a855f7",
            },
            {
              Icon: Target,
              title: lang === "pl" ? "Konkretne wiadomości" : "Ready-to-send messages",
              desc:
                lang === "pl"
                  ? "4 tony: bezpieczna, luźna, flirtująca, konkretna. Skopiuj i wyślij."
                  : "4 tones: safe, casual, flirty, specific. Copy and send.",
              hue: "#fd267a",
            },
            {
              Icon: Zap,
              title: lang === "pl" ? "Streaming na żywo" : "Live streaming",
              desc:
                lang === "pl"
                  ? "Wynik powstaje na Twoich oczach – nie czekasz w pustce."
                  : "Result generates live – no blank-screen waiting.",
              hue: "#fdb022",
            },
            {
              Icon: Shield,
              title: lang === "pl" ? "Prywatne i bezpieczne" : "Private and secure",
              desc:
                lang === "pl"
                  ? "Twoje raporty są prywatne — dostępne tylko z Twojego konta. Bez śledzenia, bez sprzedawania danych."
                  : "Your reports are private — accessible only from your account. No tracking, no data selling.",
              hue: "#32d583",
            },
            {
              Icon: Crown,
              title: lang === "pl" ? "Premium long-term" : "Premium long-term",
              desc:
                lang === "pl"
                  ? "Kompatybilność, strategia, predykcja relacji 12 mc."
                  : "Compatibility, strategy, 12-month relationship prediction.",
              hue: "#ff6036",
            },
          ].map(({ Icon, title, desc, hue }, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.5, delay: i * 0.06 }}
              className="dc-card relative overflow-hidden p-6 transition-transform hover:-translate-y-1"
            >
              <div
                className="pointer-events-none absolute -right-10 -top-10 h-28 w-28 rounded-full blur-2xl"
                style={{ background: `${hue}33` }}
              />
              <div
                className="relative mb-4 inline-flex items-center justify-center rounded-2xl p-3"
                style={{ background: `${hue}1a`, color: hue }}
              >
                <Icon className="h-5 w-5" />
              </div>
              <h3
                className="relative font-display text-lg font-semibold"
                style={{ color: "var(--dc-text)" }}
              >
                {title}
              </h3>
              <p
                className="relative mt-2 text-sm leading-relaxed"
                style={{ color: "var(--dc-muted)" }}
              >
                {desc}
              </p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* EXAMPLES */}
      <ExamplesSection lang={lang} />

      {/* TESTIMONIALS MARQUEE */}
      <TestimonialsSection lang={lang} />

      {/* FAQ */}
      <FAQSection lang={lang} />

      {/* CTA */}
      <CTASection lang={lang} onClick={proceed} />
    </div>
  );
}

/* -------------------- Music toggle -------------------- */
function MusicToggle({ enabled, onClick }) {
  const Icon = enabled ? Volume2 : VolumeX;

  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={enabled ? "Wyłącz muzykę" : "Włącz muzykę"}
      title={enabled ? "Wyłącz muzykę" : "Włącz muzykę"}
      className="fixed right-4 top-4 z-50 flex h-11 w-11 items-center justify-center rounded-full border shadow-[0_10px_30px_rgba(253,38,122,0.28)] backdrop-blur transition-all hover:-translate-y-0.5 hover:border-[#ff4458]/60 sm:right-6 sm:top-6"
      style={{
        borderColor: "var(--dc-border)",
        background: enabled
          ? "linear-gradient(135deg, rgba(255,68,88,0.95), rgba(253,38,122,0.95))"
          : "var(--dc-card)",
        color: enabled ? "#ffffff" : "var(--dc-text)",
      }}
    >
      <Icon className="h-5 w-5" />
      <span className="sr-only">{enabled ? "Wyłącz muzykę" : "Włącz muzykę"}</span>
    </button>
  );
}

/* -------------------- Section header -------------------- */
function SectionHeader({ eyebrow, title, subtitle }) {
  return (
    <div className="text-center">
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.4 }}
        className="mb-3 inline-flex items-center gap-2 rounded-full border border-[#ff4458]/30 bg-[#ff4458]/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-[#ff4458]"
      >
        <Sparkles className="h-3 w-3" />
        {eyebrow}
      </motion.div>
      <motion.h2
        initial={{ opacity: 0, y: 16 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.5, delay: 0.05 }}
        className="font-display text-3xl font-bold tracking-tight sm:text-4xl"
        style={{ color: "var(--dc-text)" }}
      >
        {title}
      </motion.h2>
      {subtitle && (
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="mx-auto mt-3 max-w-2xl text-sm"
          style={{ color: "var(--dc-muted)" }}
        >
          {subtitle}
        </motion.p>
      )}
    </div>
  );
}

/* -------------------- Animated swipe stack hero -------------------- */
const HERO_CARDS = {
  pl: [
    {
      name: "Ania, 26",
      score: 87,
      tag: "Wysokie zainteresowanie",
      tagColor: "#32d583",
      msg: "„Hej, widziałem Twoje zdjęcie z Krakowa, gdzie to dokładnie?",
      bio: "🎨 artystka · ☕ kawosz · 2 km",
      image:
        "https://images.unsplash.com/photo-1531746020798-e6953c6e8e04?crop=entropy&cs=srgb&fm=jpg&w=900&q=80",
      accent: "#ff4458",
    },
    {
      name: "Kasia, 28",
      score: 72,
      tag: "Secure attachment",
      tagColor: "#fdb022",
      msg: "„Środa 19:00, Coffeedesk Mokotowska – pasuje?",
      bio: "🏃 biegaczka · 🍷 wino · 5 km",
      image:
        "https://images.unsplash.com/photo-1632765854612-9b02b6ec2b15?crop=entropy&cs=srgb&fm=jpg&w=900&q=80",
      accent: "#a855f7",
    },
    {
      name: "Marta, 24",
      score: 64,
      tag: "Lekkie ghosting risk",
      tagColor: "#fd267a",
      msg: "„Bez presji – mam pomysł na sobotę, dam Ci znać",
      bio: "📚 czyta · 🎮 indie · 3 km",
      image:
        "https://images.pexels.com/photos/2530364/pexels-photo-2530364.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
      accent: "#0ea5e9",
    },
  ],
  en: [
    {
      name: "Ana, 26",
      score: 87,
      tag: "High interest",
      tagColor: "#32d583",
      msg: "„Hey, I saw your shot from Krakow – where exactly was that?",
      bio: "🎨 artist · ☕ coffee · 2 km",
      image:
        "https://images.unsplash.com/photo-1531746020798-e6953c6e8e04?crop=entropy&cs=srgb&fm=jpg&w=900&q=80",
      accent: "#ff4458",
    },
    {
      name: "Kate, 28",
      score: 72,
      tag: "Secure attachment",
      tagColor: "#fdb022",
      msg: "„Wed 7pm, Coffeedesk on Mokotowska – game?",
      bio: "🏃 runner · 🍷 wine · 5 km",
      image:
        "https://images.unsplash.com/photo-1632765854612-9b02b6ec2b15?crop=entropy&cs=srgb&fm=jpg&w=900&q=80",
      accent: "#a855f7",
    },
    {
      name: "Marta, 24",
      score: 64,
      tag: "Slight ghosting risk",
      tagColor: "#fd267a",
      msg: "„No pressure – I have a Saturday plan, I'll let you know",
      bio: "📚 reads · 🎮 indie · 3 km",
      image:
        "https://images.pexels.com/photos/2530364/pexels-photo-2530364.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
      accent: "#0ea5e9",
    },
  ],
};

function SwipeStackHero({ lang }) {
  const [idx, setIdx] = useState(0);
  const cards = HERO_CARDS[lang] || HERO_CARDS.pl;

  const swipe = (dir) => {
    setIdx((i) => (i + 1) % cards.length);
  };

  const visible = [
    cards[idx],
    cards[(idx + 1) % cards.length],
    cards[(idx + 2) % cards.length],
  ];

  return (
    <div className="relative" data-testid="landing-tinder-mockup">
      <div className="relative" style={{ height: 540, width: 340 }}>
        {/* stack background silhouettes */}
        <div
          className="dc-floaty absolute inset-x-3 top-2 rounded-[2rem]"
          style={{
            "--rot": "2deg",
            height: 510,
            background: "var(--dc-card-light)",
            opacity: 0.6,
          }}
        />
        <div
          className="dc-floaty absolute inset-x-6 top-4 rounded-[2rem]"
          style={{
            "--rot": "-3deg",
            height: 500,
            background: "var(--dc-card-light)",
            opacity: 0.45,
          }}
        />

        <AnimatePresence initial={false}>
          {visible
            .slice()
            .reverse()
            .map((card, revIdx) => {
              const offset = visible.length - 1 - revIdx;
              const isTop = offset === 0;
              return (
                <HeroSwipeCard
                  key={`${idx}-${offset}`}
                  card={card}
                  offset={offset}
                  isTop={isTop}
                  onSwipe={swipe}
                />
              );
            })}
        </AnimatePresence>
      </div>

      {/* action bubbles */}
      <div className="mt-5 flex items-center justify-center gap-4">
        <ActionBubble
          color="#fd267a"
          Icon={X}
          onClick={() => swipe(-1)}
        />
        <ActionBubble color="#fdb022" Icon={Star} primary onClick={() => swipe(0)} />
        <ActionBubble
          color="#32d583"
          Icon={Heart}
          onClick={() => swipe(1)}
        />
      </div>
    </div>
  );
}

function HeroSwipeCard({ card, offset, isTop, onSwipe }) {
  const x = useMotionValue(0);
  const rotate = useTransform(x, [-300, 0, 300], [-18, 0, 18]);
  const likeOp = useTransform(x, [40, 160], [0, 1]);
  const nopeOp = useTransform(x, [-160, -40], [1, 0]);

  const onEnd = (_e, info) => {
    if (Math.abs(info.offset.x) > 100 || Math.abs(info.velocity.x) > 500) {
      onSwipe(info.offset.x > 0 ? 1 : -1);
    }
  };

  const baseScale = 1 - offset * 0.05;
  const baseY = offset * 12;

  return (
    <motion.div
      className="absolute inset-0"
      drag={isTop ? "x" : false}
      dragElastic={0.7}
      dragConstraints={{ left: 0, right: 0, top: 0, bottom: 0 }}
      dragMomentum={false}
      onDragEnd={isTop ? onEnd : undefined}
      style={isTop ? { x, rotate, zIndex: 10 - offset } : { zIndex: 10 - offset }}
      initial={{ scale: baseScale - 0.05, y: baseY + 30, opacity: 0 }}
      animate={{ scale: baseScale, y: baseY, opacity: 1 }}
      exit={{
        x: x.get() >= 0 ? 700 : -700,
        rotate: x.get() >= 0 ? 28 : -28,
        opacity: 0,
        transition: { duration: 0.4, ease: [0.32, 0.72, 0, 1] },
      }}
      transition={{ type: "spring", stiffness: 260, damping: 28 }}
    >
      <div
        className="relative h-full w-full overflow-hidden rounded-[2rem] border shadow-[0_25px_60px_rgba(253,38,122,0.25)]"
        style={{
          borderColor: "var(--dc-border)",
          background: "var(--dc-card)",
          cursor: isTop ? "grab" : "default",
        }}
      >
        {/* Photo area with image background */}
        <div
          className="relative h-[62%] w-full overflow-hidden"
          style={{
            backgroundImage: card.image
              ? `url(${card.image})`
              : `linear-gradient(135deg, ${card.accent || "#ff4458"}, #fd267a, #ff6036)`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        >
          {/* Dark gradient overlay for text readability */}
          <div
            className="pointer-events-none absolute inset-0"
            style={{
              background:
                "linear-gradient(180deg, rgba(0,0,0,0.05) 0%, rgba(0,0,0,0.0) 35%, rgba(0,0,0,0.55) 80%, rgba(0,0,0,0.85) 100%)",
            }}
          />
          {/* progress dots */}
          <div className="absolute left-3 right-3 top-3 flex gap-1">
            {[0, 1, 2, 3].map((i) => (
              <span
                key={i}
                className={`h-0.5 flex-1 rounded-full ${
                  i === 0 ? "bg-white" : "bg-white/40"
                }`}
              />
            ))}
          </div>
          <div className="absolute left-4 top-6 inline-flex items-center gap-1 rounded-full border border-white/30 bg-white/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white backdrop-blur">
            <Sparkles className="h-3 w-3" /> AI Analiza
          </div>
          {/* Score chip */}
          <div className="absolute right-3 top-10 flex items-center gap-2 rounded-2xl border border-white/25 bg-black/40 px-3 py-1.5 backdrop-blur-md">
            <div className="text-right">
              <div className="font-display text-xl font-extrabold leading-none text-white">
                {card.score}
              </div>
              <div className="text-[8px] font-bold uppercase tracking-wider text-white/70">
                Match
              </div>
            </div>
          </div>

          {/* Name + age */}
          <div className="absolute inset-x-5 bottom-4 text-white">
            <h3 className="font-display text-3xl font-extrabold leading-tight drop-shadow-[0_2px_8px_rgba(0,0,0,0.6)]">
              {card.name}
            </h3>
            <p className="mt-1 text-xs text-white/85 drop-shadow">{card.bio}</p>
          </div>

          {/* LIKE/NOPE overlays */}
          {isTop && (
            <>
              <motion.div
                style={{ opacity: likeOp }}
                className="pointer-events-none absolute left-4 top-16 -rotate-12 rounded-2xl border-4 border-[#32d583] px-3 py-1"
              >
                <span className="font-display text-xl font-extrabold tracking-wider text-[#32d583]">
                  LIKE
                </span>
              </motion.div>
              <motion.div
                style={{ opacity: nopeOp }}
                className="pointer-events-none absolute right-4 top-16 rotate-12 rounded-2xl border-4 border-[#fd267a] px-3 py-1"
              >
                <span className="font-display text-xl font-extrabold tracking-wider text-[#fd267a]">
                  NOPE
                </span>
              </motion.div>
            </>
          )}
        </div>

        {/* AI insight area */}
        <div className="space-y-2 p-4">
          <div
            className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider"
            style={{ background: `${card.tagColor}22`, color: card.tagColor }}
          >
            <Sparkles className="h-3 w-3" /> {card.tag}
          </div>
          <div
            className="rounded-xl border px-2.5 py-1.5 text-[12px]"
            style={{
              borderColor: "rgba(255,68,88,0.3)",
              background: "rgba(255,68,88,0.06)",
              color: "var(--dc-text)",
            }}
          >
            💬 {card.msg}
          </div>
        </div>
      </div>
    </motion.div>
  );
}

function ActionBubble({ color, Icon, primary = false, onClick }) {
  const size = primary ? "h-14 w-14" : "h-12 w-12";
  return (
    <button
      type="button"
      onClick={onClick}
      className={`${size} flex items-center justify-center rounded-full border-2 shadow-xl transition-transform hover:-translate-y-0.5 active:scale-95`}
      style={{
        borderColor: color,
        color,
        background: "var(--dc-card)",
        boxShadow: `0 12px 30px ${color}33`,
      }}
    >
      <Icon
        className="h-5 w-5"
        fill={primary ? color : "currentColor"}
        strokeWidth={primary ? 2 : 2.5}
      />
    </button>
  );
}

/* -------------------- Examples (mock report cards) -------------------- */
function ExamplesSection({ lang }) {
  const examples = [
    {
      title: lang === "pl" ? "Pierwsza wiadomość" : "First message",
      tag: lang === "pl" ? "Bezpieczna" : "Safe",
      score: 78,
      text:
        lang === "pl"
          ? "„Hej Ania, słuchaj – jak Twoje plany w środę wieczorem? Znam świetne miejsce na kawę przy Mokotowskiej."
          : "„Hey Ana, what about Wednesday evening? I know a great coffee spot.",
      hue: "#3b82f6",
    },
    {
      title: lang === "pl" ? "Flirtująca propozycja" : "Flirty pitch",
      tag: lang === "pl" ? "Flirtująca" : "Flirty",
      score: 84,
      text:
        lang === "pl"
          ? "„Mam plan: środa, mała kawiarnia, ja, Ty i Twój uśmiech, który mnie ciekawi z daleka."
          : "„My plan: Wednesday, small café, you, me, and that smile that intrigues me from afar.",
      hue: "#fd267a",
    },
    {
      title: lang === "pl" ? "Konkretne miejsce" : "Specific place",
      tag: lang === "pl" ? "Konkretna" : "Specific",
      score: 91,
      text:
        lang === "pl"
          ? "„Sobota 18:00, Cosmo Hala Koszyki – burgery i wino. Wpadasz?"
          : "„Saturday 6pm, Cosmo Hala Koszyki – burgers & wine. In?",
      hue: "#fdb022",
    },
  ];
  return (
    <section
      className="mx-auto max-w-6xl px-4 pb-20 sm:px-6 lg:px-8"
      data-testid="landing-examples"
    >
      <SectionHeader
        eyebrow={lang === "pl" ? "Przykłady" : "Examples"}
        title={
          lang === "pl"
            ? "Co dostaniesz po analizie"
            : "What you get from an analysis"
        }
        subtitle={
          lang === "pl"
            ? "Gotowe wiadomości w czterech tonach – do skopiowania."
            : "Ready-made messages in four tones – copy and send."
        }
      />
      <div className="mt-10 grid grid-cols-1 gap-5 md:grid-cols-3">
        {examples.map((ex, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.5, delay: i * 0.08 }}
            className="dc-card relative flex flex-col overflow-hidden p-0"
          >
            <div
              className="relative flex h-32 items-end p-5"
              style={{
                background: `linear-gradient(135deg, ${ex.hue}, ${ex.hue}aa)`,
              }}
            >
              <div className="flex w-full items-end justify-between text-white">
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-wider opacity-80">
                    {ex.tag}
                  </div>
                  <h3 className="font-display text-xl font-extrabold">
                    {ex.title}
                  </h3>
                </div>
                <div className="rounded-2xl border border-white/30 bg-black/30 px-2.5 py-1.5 backdrop-blur-md">
                  <div className="font-display text-lg font-extrabold leading-none">
                    {ex.score}
                  </div>
                  <div className="text-[8px] font-bold uppercase tracking-wider opacity-80">
                    Score
                  </div>
                </div>
              </div>
            </div>
            <div className="flex-1 p-5">
              <p
                className="text-sm leading-relaxed"
                style={{ color: "var(--dc-text)" }}
              >
                {ex.text}
              </p>
            </div>
            <div
              className="flex items-center justify-between border-t px-5 py-3"
              style={{ borderColor: "var(--dc-border)" }}
            >
              <span
                className="text-[11px]"
                style={{ color: "var(--dc-muted)" }}
              >
                AI Dating Coach
              </span>
              <span
                className="inline-flex items-center gap-1 text-[11px] font-semibold"
                style={{ color: ex.hue }}
              >
                <Check className="h-3 w-3" /> {lang === "pl" ? "Gotowe" : "Ready"}
              </span>
            </div>
          </motion.div>
        ))}
      </div>
    </section>
  );
}

/* -------------------- Testimonials marquee -------------------- */
function TestimonialsSection({ lang }) {
  const items = [
    {
      name: "Tomek W.",
      role: lang === "pl" ? "26 lat, Warszawa" : "26, Warsaw",
      text:
        lang === "pl"
          ? "Po 3 latach singla, pierwsza randka w 2 tygodnie. Brzmi jak reklama, ale serio."
          : "After 3 single years, first date in 2 weeks. Sounds like an ad, but it's real.",
    },
    {
      name: "Karol M.",
      role: lang === "pl" ? "31 lat, Kraków" : "31, Krakow",
      text:
        lang === "pl"
          ? "Pierwszy raz wyłapałem dlaczego ghostowali mnie. Naprawione w jednej rozmowie."
          : "First time I understood why people ghosted me. Fixed in one chat.",
    },
    {
      name: "Anna K.",
      role: lang === "pl" ? "29 lat, Wrocław" : "29, Wroclaw",
      text:
        lang === "pl"
          ? "Premium pokazało, że szukałam ludzi, którzy do mnie nie pasują. Game changer."
          : "Premium showed me I was chasing people who don't fit. Game changer.",
    },
    {
      name: "Michał R.",
      role: lang === "pl" ? "34 lat, Poznań" : "34, Poznan",
      text:
        lang === "pl"
          ? "Wiadomości brzmią jak moje – tylko o 200% lepiej napisane."
          : "Messages sound like mine – but written 200% better.",
    },
    {
      name: "Julia B.",
      role: lang === "pl" ? "27 lat, Łódź" : "27, Lodz",
      text:
        lang === "pl"
          ? "AI Vision na zdjęciach to czary. Wiedziało więcej o nim niż jego biografia."
          : "AI Vision on photos is magic. Knew more about him than his own bio.",
    },
    {
      name: "Piotr S.",
      role: lang === "pl" ? "29 lat, Gdańsk" : "29, Gdansk",
      text:
        lang === "pl"
          ? "4 randki w miesiąc. Wcześniej miałem 0 w pół roku."
          : "4 dates in a month. Used to be 0 in half a year.",
    },
  ];
  // duplicate for smooth marquee
  const list = [...items, ...items];

  return (
    <section
      className="relative overflow-hidden pb-20"
      data-testid="landing-testimonials"
    >
      <SectionHeader
        eyebrow={lang === "pl" ? "Opinie" : "Reviews"}
        title={
          lang === "pl"
            ? "Realni ludzie, realne rezultaty"
            : "Real people, real results"
        }
        subtitle={
          lang === "pl"
            ? "Wybrane wiadomości od użytkowników, którzy zgodzili się je upublicznić."
            : "Selected messages from users who agreed to publish them."
        }
      />
      <div className="mt-10">
        <div className="dc-marquee">
          {list.map((it, i) => (
            <div key={i} className="mx-3 w-[320px] flex-shrink-0">
              <div
                className="dc-card flex h-full flex-col gap-3 p-5"
                style={{ borderColor: "var(--dc-border)" }}
              >
                <Quote className="h-5 w-5 text-[#ff4458]/60" />
                <p
                  className="text-sm leading-relaxed"
                  style={{ color: "var(--dc-text)" }}
                >
                  {it.text}
                </p>
                <div
                  className="mt-auto flex items-center gap-3 border-t pt-3"
                  style={{ borderColor: "var(--dc-border)" }}
                >
                  <div className="flex h-9 w-9 items-center justify-center rounded-full dc-gradient-bg font-display text-xs font-bold text-white">
                    {it.name
                      .split(" ")
                      .map((s) => s[0])
                      .join("")}
                  </div>
                  <div>
                    <div
                      className="text-sm font-semibold"
                      style={{ color: "var(--dc-text)" }}
                    >
                      {it.name}
                    </div>
                    <div
                      className="text-[11px]"
                      style={{ color: "var(--dc-muted)" }}
                    >
                      {it.role}
                    </div>
                  </div>
                  <div className="ml-auto flex">
                    {[0, 1, 2, 3, 4].map((s) => (
                      <Star
                        key={s}
                        className="h-3 w-3 text-[#fdb022]"
                        fill="#fdb022"
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* -------------------- FAQ accordion -------------------- */
function FAQSection({ lang }) {
  const items =
    lang === "pl"
      ? [
          {
            q: "Czy moje rozmowy są bezpieczne?",
            a: "Tak. Twoje raporty są zapisane na Twoim koncie i nikt inny ich nie widzi. Nie sprzedajemy danych stronom trzecim poza wybranym dostawcą AI.",
          },
          {
            q: "Czy AI naprawdę 'wie' co napisać?",
            a: "AI uczy się na milionach rzeczywistych interakcji randkowych. Plus mamy dużą bazę wzorców utrzymywaną przez coachy randkowych jako fallback i wzbogacenie wyników.",
          },
          {
            q: "Premium vs Standard – co dostaję?",
            a: "Standard: ocena rozmowy, 4 wiadomości, tipsy. Premium: pełna osobowość (Big Five), styl przywiązania, kompatybilność, strategia 12 miesięcy.",
          },
          {
            q: "Czy muszę mieć konto?",
            a: "Tak – konto trzyma Twoje raporty, kredyty i subskrypcję w bezpiecznym, prywatnym miejscu. Rejestracja jest darmowa i daje 1 kredyt premium na start.",
          },
          {
            q: "Czy działa w innych językach?",
            a: "Tak – polski i angielski. Dostosowuje też ton kulturowy: bezpośredni vs grzecznościowy.",
          },
        ]
      : [
          {
            q: "Are my conversations safe?",
            a: "Yes. Your reports are saved to your account and nobody else can see them. We don't share data with third parties except the chosen AI provider.",
          },
          {
            q: "Does AI really 'know' what to write?",
            a: "AI learns from millions of real dating interactions. Plus we maintain a large pattern database curated by dating coaches as a fallback and enrichment layer.",
          },
          {
            q: "Premium vs Standard – what do I get?",
            a: "Standard: chat scoring, 4 messages, tips. Premium: full personality (Big Five), attachment style, compatibility, 12-month strategy.",
          },
          {
            q: "Do I need an account?",
            a: "Yes – an account keeps your reports, credits and subscription in a private, secure place. Sign-up is free and includes 1 premium credit to start.",
          },
          {
            q: "Does it work in other languages?",
            a: "Yes – Polish and English, with culturally tuned tone: direct vs polite.",
          },
        ];
  const [open, setOpen] = useState(0);
  return (
    <section
      className="mx-auto max-w-3xl px-4 pb-20 sm:px-6 lg:px-8"
      data-testid="landing-faq"
    >
      <SectionHeader
        eyebrow="FAQ"
        title={lang === "pl" ? "Najczęstsze pytania" : "Frequent questions"}
      />
      <div className="mt-8 space-y-3">
        {items.map((it, i) => {
          const isOpen = open === i;
          return (
            <div
              key={i}
              className="dc-card overflow-hidden"
              style={{ borderColor: "var(--dc-border)" }}
            >
              <button
                type="button"
                onClick={() => setOpen(isOpen ? -1 : i)}
                className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left"
              >
                <span
                  className="font-display text-sm font-semibold sm:text-base"
                  style={{ color: "var(--dc-text)" }}
                >
                  {it.q}
                </span>
                <ChevronDown
                  className={`h-4 w-4 flex-shrink-0 transition-transform ${
                    isOpen ? "rotate-180 text-[#ff4458]" : ""
                  }`}
                  style={{ color: isOpen ? undefined : "var(--dc-muted)" }}
                />
              </button>
              <AnimatePresence initial={false}>
                {isOpen && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.25 }}
                    className="overflow-hidden"
                  >
                    <p
                      className="px-5 pb-4 text-sm leading-relaxed"
                      style={{ color: "var(--dc-muted)" }}
                    >
                      {it.a}
                    </p>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
        })}
      </div>
    </section>
  );
}

/* -------------------- Bottom CTA -------------------- */
function CTASection({ lang, onClick }) {
  return (
    <section
      className="mx-auto max-w-5xl px-4 pb-24 sm:px-6 lg:px-8"
      data-testid="landing-cta"
    >
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.5 }}
        className="relative overflow-hidden rounded-[2rem] border p-10 text-center sm:p-14"
        style={{
          borderColor: "var(--dc-border)",
          background:
            "linear-gradient(135deg, rgba(255,68,88,0.18), rgba(253,38,122,0.12), rgba(255,96,54,0.18))",
        }}
      >
        <div
          className="pointer-events-none absolute -right-32 -top-32 h-72 w-72 rounded-full blur-3xl"
          style={{ background: "rgba(255,68,88,0.3)" }}
        />
        <div
          className="pointer-events-none absolute -bottom-24 -left-24 h-60 w-60 rounded-full blur-3xl"
          style={{ background: "rgba(253,176,34,0.25)" }}
        />
        <div className="relative">
          <h2
            className="font-display text-3xl font-bold tracking-tight sm:text-4xl"
            style={{ color: "var(--dc-text)" }}
          >
            {lang === "pl"
              ? "Następna rozmowa? Wreszcie z planem."
              : "Next chat? Finally with a plan."}
          </h2>
          <p
            className="mx-auto mt-4 max-w-2xl text-sm sm:text-base"
            style={{ color: "var(--dc-muted)" }}
          >
            {lang === "pl"
              ? "Wrzuć screen, dostań konkretną wiadomość w 30 sekund. Załóż darmowe konto i dostań 1 kredyt premium."
              : "Drop a screenshot, get a ready message in 30 seconds. Sign up free and get 1 premium credit."}
          </p>
          <button
            type="button"
            onClick={onClick}
            className="mt-7 inline-flex items-center gap-2 rounded-full dc-gradient-bg px-7 py-3.5 font-display text-base font-semibold text-white shadow-[0_12px_40px_rgba(253,38,122,0.45)] transition-all hover:-translate-y-0.5 hover:shadow-[0_18px_50px_rgba(253,38,122,0.6)]"
          >
            <Sparkles className="h-4 w-4" />
            {lang === "pl" ? "Zacznij teraz – darmowo" : "Start now – free"}
            <ArrowRight className="h-4 w-4" />
          </button>
          <div
            className="mt-4 inline-flex items-center gap-4 text-[11px]"
            style={{ color: "var(--dc-muted)" }}
          >
            <span className="inline-flex items-center gap-1">
              <Lock className="h-3 w-3" /> {lang === "pl" ? "Prywatnie" : "Private"}
            </span>
            <span className="inline-flex items-center gap-1">
              <Zap className="h-3 w-3" /> {lang === "pl" ? "30 sek" : "30 sec"}
            </span>
            <span className="inline-flex items-center gap-1">
              <Check className="h-3 w-3" /> {lang === "pl" ? "1 kredyt gratis" : "1 free credit"}
            </span>
          </div>
        </div>
      </motion.div>
    </section>
  );
}
